<template>
  <HrPageLayout
    title="Correspondences"
    subtitle="County HR policy circulars, manuals, CBAs, disciplinary references, and dated updates — Secretariat register."
    icon=""
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'County HRMIS (SR)' },
      { label: 'Correspondences' }
    ]"
  >
    <div v-if="!allowed" class="corr-access-denied">
      <i class="bx bx-lock-alt"></i>
      You do not have access to this module.
    </div>
    <div v-else class="corr-shell">
      <section class="corr-hero" aria-label="Introduction">
        <p class="corr-lead">
          Maintain a controlled library of correspondence and guidance to departments: classify by type,
          attach authoritative PDF copies, tag matters arising or version notes, and record publication dates for audit.
        </p>
      </section>

      <header class="corr-toolbar">
        <h2 class="corr-toolbar-title">Register</h2>
        <div class="corr-toolbar-actions">
          <button v-if="canManage" type="button" class="corr-btn corr-btn--primary" @click="openForm()">
            <i class="bx bx-plus-circle"></i> Add correspondence
          </button>
          <button type="button" class="corr-btn corr-btn--outline" @click="loadList">
            <i class="bx bx-refresh"></i> Refresh
          </button>
        </div>
      </header>

      <div class="corr-table-card">
        <div v-if="loading" class="corr-loading"><i class="bx bx-loader-alt bx-spin"></i> Loading records…</div>
        <div v-else class="corr-table-scroll">
          <table class="corr-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Subject / title</th>
                <th>Matters arising / notes</th>
                <th>Publication date</th>
                <th>Status</th>
                <th>File</th>
                <th v-if="canManage" class="corr-th-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in list" :key="r.id">
                <td><span class="corr-pill corr-pill--type">{{ docTypeLabel(r.doc_type) }}</span></td>
                <td class="corr-strong corr-clip">{{ r.title || '–' }}</td>
                <td class="corr-muted corr-clip">{{ r.version_label || '–' }}</td>
                <td>{{ formatPublished(r.published_at) }}</td>
                <td>
                  <span class="corr-pill" :class="activeClass(r.is_active)">{{ activeLabel(r.is_active) }}</span>
                </td>
                <td>
                  <a v-if="r.file_path" class="corr-link" :href="fileUrl(r.file_path)" target="_blank" rel="noopener">
                    <i class="bx bx-file-blank"></i> PDF
                  </a>
                  <span v-else class="corr-muted">–</span>
                </td>
                <td v-if="canManage" class="corr-td-actions">
                  <button type="button" class="corr-btn corr-btn--sm corr-btn--ghost" @click="openForm(r)">Edit</button>
                </td>
              </tr>
              <tr v-if="!list.length">
                <td :colspan="canManage ? 7 : 6" class="corr-empty">No correspondence records.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="total > limit" class="corr-pagination">
          <button type="button" class="corr-btn corr-btn--sm corr-btn--outline" :disabled="page <= 1" @click="page--; loadList()">
            Previous
          </button>
          <span class="corr-page-info">Page {{ page }} / {{ totalPages }}</span>
          <button type="button" class="corr-btn corr-btn--sm corr-btn--outline" :disabled="page >= totalPages" @click="page++; loadList()">
            Next
          </button>
        </div>
      </div>

      <div v-if="showModal" class="corr-modal-overlay" @click.self="showModal = false">
        <div class="corr-modal" role="dialog" aria-modal="true">
          <div class="corr-modal-head">
            <h3 class="corr-modal-title">{{ form.id ? 'Edit correspondence' : 'New correspondence' }}</h3>
          </div>
          <div class="corr-modal-body">
            <div class="corr-field">
              <label class="corr-label">Document type</label>
              <select v-model="form.doc_type" class="corr-input">
                <option value="circular">Circular</option>
                <option value="manual">Manual</option>
                <option value="cba">CBA</option>
                <option value="disciplinary_procedure">Disciplinary procedure</option>
              </select>
            </div>
            <div class="corr-field">
              <label class="corr-label">Subject / title <span class="corr-req">*</span></label>
              <input v-model="form.title" type="text" class="corr-input" placeholder="Official subject line">
            </div>
            <div class="corr-field">
              <label class="corr-label">Matters arising / update notes</label>
              <input v-model="form.version_label" type="text" class="corr-input" placeholder="Brief update or cross-reference">
            </div>
            <div class="corr-field">
              <label class="corr-label">Upload PDF</label>
              <input
                ref="fileInput"
                type="file"
                class="corr-input corr-input-file"
                accept=".pdf,application/pdf"
                @change="onFileChange"
              >
              <p class="corr-field-hint">Replace file on edit by choosing a new PDF.</p>
            </div>
            <div class="corr-field">
              <label class="corr-label">Correspondence date</label>
              <input v-model="form.published_at" type="date" class="corr-input">
            </div>
            <div class="corr-field">
              <label class="corr-label">Active</label>
              <select v-model="form.is_active" class="corr-input">
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
          </div>
          <div class="corr-modal-footer">
            <button type="button" class="corr-btn corr-btn--outline" @click="showModal = false">Cancel</button>
            <button type="button" class="corr-btn corr-btn--primary" :disabled="saving" @click="saveForm">{{ saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight } from "@/api";

const SAVE_FN = "saveHrKnowledgeDocument";
const LIST_FN = "getHrKnowledgeDocumentList";

export default {
  name: "ModuleHrKnowledge",
  components: { HrPageLayout },
  data() {
    return {
      allowed: false,
      canManage: false,
      loading: false,
      saving: false,
      showModal: false,
      list: [],
      total: 0,
      page: 1,
      limit: 50,
      form: {}
    };
  },
  computed: {
    totalPages() {
      return this.limit > 0 ? Math.ceil(this.total / this.limit) || 1 : 1;
    }
  },
  created() {
    this.allowed = getRight("HR_KNOWLEDGE_VIEW") || getRight("HR_KNOWLEDGE_MANAGE");
    this.canManage = getRight("HR_KNOWLEDGE_MANAGE");
    if (this.allowed) {
      this.resetForm();
      this.loadList();
    }
  },
  methods: {
    resetForm() {
      this.form = {
        id: null,
        doc_type: "circular",
        title: "",
        version_label: "",
        file_path: null,
        published_at: "",
        is_active: "1"
      };
    },
    docTypeLabel(t) {
      const k = String(t || "").trim().toLowerCase();
      const map = {
        circular: "Circular",
        manual: "Manual",
        cba: "CBA",
        disciplinary_procedure: "Disciplinary procedure"
      };
      return map[k] || (t ? String(t).replace(/_/g, " ") : "–");
    },
    activeLabel(v) {
      return String(v) === "1" || v === 1 ? "Active" : "Inactive";
    },
    activeClass(v) {
      return String(v) === "1" || v === 1 ? "corr-pill--success" : "corr-pill--muted";
    },
    formatPublished(d) {
      if (d == null || d === "") return "–";
      const x = String(d).slice(0, 10);
      return x || "–";
    },
    fileUrl(path) {
      const p = String(path || "");
      if (!p) return "#";
      if (/^https?:\/\//i.test(p)) return p;
      const base = authUrl.replace(/\/?index\.php$/i, "/");
      return base + p.replace(/^\/+/, "");
    },

    loadList() {
      this.loading = true;
      const fd = new FormData();
      fd.append("function", LIST_FN);
      fd.append("limit", String(this.limit));
      fd.append("page", String(this.page));
      execute(fd, authUrl)
        .then((res) => {
          this.loading = false;
          const payload = res.data && res.data.success && res.data.data ? res.data.data : {};
          this.list = payload.data || [];
          this.total = payload.total || 0;
          const pm = payload.permissions || {};
          this.canManage = !!pm.manage || getRight("HR_KNOWLEDGE_MANAGE");
        })
        .catch(() => {
          this.loading = false;
          this.list = [];
        });
    },

    openForm(row) {
      if (row) {
        this.form = {
          id: row.id,
          doc_type: row.doc_type || "circular",
          title: row.title || "",
          version_label: row.version_label || "",
          file_path: null,
          published_at: row.published_at ? String(row.published_at).slice(0, 10) : "",
          is_active: String(row.is_active != null ? row.is_active : "1")
        };
      } else {
        this.resetForm();
      }
      this.showModal = true;
      this.$nextTick(() => {
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      });
    },

    onFileChange(e) {
      const f = e.target.files && e.target.files[0];
      this.form.file_path = f || null;
    },

    saveForm() {
      if (!String(this.form.title || "").trim()) {
        alert("Subject / title is required.");
        return;
      }
      this.saving = true;
      const fd = new FormData();
      fd.append("function", SAVE_FN);
      if (this.form.id) fd.append("id", String(this.form.id));

      fd.append("doc_type", this.form.doc_type || "");
      fd.append("title", this.form.title || "");
      fd.append("version_label", this.form.version_label || "");
      fd.append("published_at", this.form.published_at || "");
      fd.append("is_active", this.form.is_active != null ? String(this.form.is_active) : "");

      const file = this.form.file_path;
      if (file && typeof file !== "string") fd.append("file_path", file);

      execute(fd, authUrl)
        .then((res) => {
          this.saving = false;
          if (res.data && res.data.success) {
            this.showModal = false;
            this.loadList();
          } else {
            alert(res.data && res.data.message ? res.data.message : "Save failed");
          }
        })
        .catch(() => { this.saving = false; });
    }
  }
};
</script>

<style scoped>
/* Institutional shell — mirrors Cabinet module */
.corr-shell {
  max-width: 1180px;
  margin: 0 auto;
  padding-bottom: 40px;
}

.corr-access-denied {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 22px;
  border-radius: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 14px;
}

.corr-hero {
  background: linear-gradient(135deg, #0d4f73 0%, #1478b0 55%, #0f5980 100%);
  color: #fff;
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 20px;
  box-shadow: 0 10px 32px rgba(13, 79, 115, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.corr-lead {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.95;
}

.corr-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.corr-toolbar-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.corr-toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.corr-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
}

.corr-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.corr-btn--primary {
  background: linear-gradient(135deg, #2195d3 0%, #0d4f73 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(33, 149, 211, 0.35);
}

.corr-btn--outline {
  background: #fff;
  color: #0d4f73;
  border: 2px solid #94a3b8;
}

.corr-btn--ghost {
  background: transparent;
  color: #0d4f73;
  border: 1px solid #cbd5e1;
}

.corr-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
}

.corr-btn--sm {
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 8px;
}

.corr-table-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 18px rgba(13, 79, 115, 0.07);
  overflow: hidden;
}

.corr-table-scroll {
  overflow-x: auto;
}

.corr-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.corr-table thead {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.corr-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.corr-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: middle;
}

.corr-table tbody tr:hover {
  background: #fafbfc;
}

.corr-th-actions,
.corr-td-actions {
  width: 96px;
  text-align: right;
  white-space: nowrap;
}

.corr-strong {
  font-weight: 600;
  color: #0f172a;
}

.corr-muted {
  color: #64748b;
  font-size: 13px;
}

.corr-clip {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.corr-empty {
  text-align: center;
  color: #94a3b8;
  padding: 28px 16px !important;
  font-size: 14px;
}

.corr-loading {
  text-align: center;
  padding: 28px;
  color: #64748b;
  font-size: 14px;
}

.corr-loading .bx-spin {
  vertical-align: middle;
  margin-right: 8px;
}

.corr-pill {
  display: inline-block;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.corr-pill--type {
  background: #e0f2fe;
  color: #0369a1;
}

.corr-pill--success {
  background: #ecfdf5;
  color: #047857;
}

.corr-pill--muted {
  background: #f1f5f9;
  color: #64748b;
}

.corr-link {
  color: #0d4f73;
  font-weight: 600;
  text-decoration: none;
}

.corr-link:hover {
  text-decoration: underline;
}

.corr-pagination {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.corr-page-info {
  font-size: 13px;
  color: #64748b;
}

/* Modal */
.corr-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 16px;
}

.corr-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 540px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(13, 79, 115, 0.2);
  border: 1px solid #e2e8f0;
}

.corr-modal-head {
  padding: 18px 22px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
}

.corr-modal-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #0d4f73;
}

.corr-modal-body {
  padding: 18px 22px;
  overflow-y: auto;
  flex: 1;
}

.corr-modal-footer {
  padding: 14px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: #fafbfc;
}

.corr-field {
  margin-bottom: 14px;
}

.corr-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.corr-req {
  color: #b91c1c;
}

.corr-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #fff;
}

.corr-input:focus {
  outline: none;
  border-color: #2195d3;
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.2);
}

.corr-input-file {
  padding: 8px;
}

.corr-field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #64748b;
}
</style>
